import { Button } from '@aecom-ai-engineering/react-components';
import { useMyUser } from '@consigli/hooks';
import clsx from 'clsx';
import { ArrowUp } from 'lucide-react';
import {
  type KeyboardEventHandler,
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FC,
  memo,
} from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureFlag, useFeatureFlag } from '@/feature-flags';
import { type Message } from '@/util/interfaces';

import { AddContextAttachments } from './add-context/add-context-attachments';
import { AddContextModal } from './add-context/add-context-modal';
import { AddContextMenu } from './add-context-menu';
import { type AddContextMenuKind, type AddContextSelection, type PropChatVariant } from './types';
import { useAgentChatAttachments } from './use-agent-chat-attachments';

interface ChatInputProps {
  disabled: boolean;
  loading: boolean;
  loadingMessage: boolean;
  /**
   * Called with what the user picked in the add-context modal. A notification
   * only - what is attached reaches `/query` through the attachments store,
   * not through here - and nothing supplies it today.
   */
  onAddContext?: (selection: AddContextSelection) => void;
  onMessage: (message: Message) => void;
  variant: PropChatVariant;
}

/*
 * How many attachment chips the collapsed strip shows. The drawer has room for
 * one beside the count and the chevron; fullscreen centres the input in a
 * 640px column, which splits into three.
 */
const DRAWER_COLLAPSED_CHIPS = 1;
const FULLSCREEN_COLLAPSED_CHIPS = 3;

const ChatInputComponent: FC<ChatInputProps> = ({
  disabled,
  loading,
  loadingMessage,
  onAddContext,
  onMessage,
  variant,
}) => {
  const { t } = useTranslation();
  const { user } = useMyUser();
  const [message, setMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputHintId = useId();
  const inputRowRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Which list the modal shows, and whether it is open at all.
  const [contextKind, setContextKind] = useState<AddContextMenuKind | null>(null);
  /*
   * What is attached to the next question lives above the router's Outlet, not
   * here: this composer unmounts with the drawer, and a finding can be attached
   * from its card on the page behind it. The session provider reads the same
   * store when it builds a question's `context`.
   */
  const { attachments, attachedKind, setContext, clearContext, removeDocument, removeFinding } =
    useAgentChatAttachments();
  const isContextEnabled = useFeatureFlag(FeatureFlag.PropChatContext);

  // Derived, not stored: a send made with the mouse blurs the field before it
  // clears the text, so a stored flag would latch on the pre-send state and
  // leave the composer expanded while empty and unfocused.
  const isDrawerExpanded = isFocused || message !== '';
  // Shift+Enter can leave nothing but line breaks, which would send an empty bubble.
  const hasText = message.trim() !== '';

  /*
   * Grows the row with what is typed, wrapped lines and Shift+Enter alike,
   * rather than scrolling the first lines out of sight. Only once the text
   * outgrows the expanded row's own height: below that the row keeps its class
   * height, which is what the expand animates, and the field stretches to fill
   * it, so a click anywhere in the row still lands in the field. The field's
   * max-height takes over past a long paste, and from there it scrolls.
   */
  useLayoutEffect(() => {
    const row = inputRowRef.current;
    const textarea = textareaRef.current;
    if (!row || !textarea) return;

    const fit = () => {
      row.style.height = '';
      textarea.style.height = '';
      if (isDrawerExpanded && textarea.scrollHeight > textarea.clientHeight) {
        row.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    };

    /*
     * Mid-expand the field is stretched to a row still on its way up, so even
     * an empty one reads as overflowing it and the row would snap to its
     * content, then jump again when the next character handed it back to the
     * class. Measure once the row has arrived instead.
     */
    const isResizing = row
      .getAnimations?.()
      .some(
        (animation) =>
          animation instanceof CSSTransition && animation.transitionProperty === 'height',
      );
    if (!isResizing) {
      fit();
      return;
    }

    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.target === row && event.propertyName === 'height') fit();
    };
    row.addEventListener('transitionend', onTransitionEnd);
    return () => row.removeEventListener('transitionend', onTransitionEnd);
  }, [message, isDrawerExpanded]);

  const sendMessage = useCallback(() => {
    if (!hasText) return;

    onMessage({
      id: crypto.randomUUID(),
      body: message,
      authorId: user.id,
      createdAt: new Date().toISOString(),
    });

    setMessage('');
  }, [user, message, hasText, onMessage]);

  const onKeyDown = useCallback<KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>>(
    (event) => {
      const { key, metaKey, ctrlKey, altKey, shiftKey } = event;
      if (key !== 'Enter') return;
      // Page shortcuts listen for Enter on window and cancel it, the dashboard's
      // search among them, which stopped Shift+Enter making its line. The
      // composer owns the key while it has focus.
      event.stopPropagation();
      // An IME confirms its candidate with Enter, which is not the user sending.
      if (event.nativeEvent.isComposing) return;
      if (!metaKey && !ctrlKey && !altKey && !shiftKey) {
        event.preventDefault();
        sendMessage();
      }
    },
    [sendMessage],
  );

  const onSendButtonKeyDown = useCallback<KeyboardEventHandler<HTMLButtonElement>>(
    (event) => {
      if (event.key !== 'Enter') return;

      event.preventDefault();
      event.stopPropagation();
      sendMessage();
    },
    [sendMessage],
  );

  const inputDisabled = disabled || loading || loadingMessage;
  const isFullscreen = variant === 'fullscreen';
  const hasAttachments =
    isContextEnabled && attachments.findings.length + attachments.documents.length > 0;

  return (
    <div
      className={clsx(
        'flex shrink-0 flex-col gap-[var(--ds-space-2)] bg-[var(--ds-color-bg-primary)]',
        /*
         * The rule only belongs to the drawer. Fullscreen centres the input in
         * a 640px column, so a border across it reads as a line floating in the
         * middle of the page rather than the panel's own divider - and the chip
         * frames are all drawn against the drawer.
         */
        hasAttachments &&
          !isFullscreen &&
          'border-t border-solid border-[var(--ds-color-border-primary)]',
        isFullscreen
          ? 'mx-auto w-[calc(100%-var(--ds-space-8))] max-w-[640px] pb-[var(--ds-space-6)] pt-[var(--ds-space-3)]'
          : 'px-[var(--ds-space-4)] py-[var(--ds-space-3)]',
      )}
    >
      <span id={inputHintId} className="sr-only">
        {t('prop-chat.input-hint')}
      </span>
      {isContextEnabled && (
        <AddContextAttachments
          collapsedChipCount={isFullscreen ? FULLSCREEN_COLLAPSED_CHIPS : DRAWER_COLLAPSED_CHIPS}
          documents={attachments.documents}
          findings={attachments.findings}
          onClearAll={clearContext}
          onRemoveDocument={removeDocument}
          onRemoveFinding={removeFinding}
        />
      )}
      <div
        ref={inputRowRef}
        className={clsx(
          /*
           * focus-within, so the brand border stays while focus is on the plus
           * or the send CTA too. Hover is held off a focused row: Tailwind
           * emits hover after focus-within, so it would win while the pointer
           * rests on the field being typed in. The halo is the same brand
           * colour thinned out, a ring and a blur, so the edge fades rather
           * than stopping at the border.
           */
          'w-full gap-[var(--ds-space-2)] rounded-[var(--ds-radius-10)] border border-solid bg-[var(--ds-color-bg-primary)] transition-[height,border-color,box-shadow] duration-300 motion-reduce:transition-none hover:not-focus-within:border-[var(--ds-color-border-secondary)] focus-within:border-[var(--ds-color-border-brand)] focus-within:shadow-[0_0_0_var(--ds-space-0-75)_color-mix(in_srgb,var(--ds-color-border-brand)_18%,transparent),0_0_var(--ds-space-2)_0_color-mix(in_srgb,var(--ds-color-border-brand)_20%,transparent)]',
          isFullscreen
            ? 'p-[calc(var(--ds-space-4)+var(--ds-space-0-25))]'
            : 'py-[calc(var(--ds-space-1-5)+var(--ds-space-0-25))] pl-[calc(var(--ds-space-3)+var(--ds-space-0-25))] pr-[calc(var(--ds-space-1-5)+var(--ds-space-0-25))]',
          isDrawerExpanded
            ? /*
               * Expanded, the message gets a row to itself above the controls,
               * so it starts at the row's left edge rather than being indented
               * by the plus and shortened by the send CTA. A grid rather than a
               * second element around the controls: the textarea has to stay
               * the same node across the collapse, or it would remount and drop
               * the focus that expanded the row in the first place.
               */
              'grid h-[var(--ds-space-24)] grid-cols-[1fr_auto] grid-rows-[1fr_auto] items-stretch border-[var(--ds-color-border-secondary)]'
            : clsx(
                'flex items-center border-[var(--ds-color-border-primary)]',
                isFullscreen
                  ? 'h-[calc(var(--ds-space-12)+var(--ds-space-3))]'
                  : 'h-[var(--ds-space-10)]',
              ),
        )}
      >
        {isContextEnabled && (
          <AddContextMenu
            anchorRef={inputRowRef}
            attachedCount={attachments.findings.length + attachments.documents.length}
            attachedKind={attachedKind}
            /*
             * justify-self so the wrapper span does not stretch across the row it
             * shares with the send CTA, which would hand its tooltip the whole
             * width to open from.
             *
             * self-center, not self-end: the plus is a bare 16px glyph while the
             * send CTA is the same glyph centred in a 26px button, so aligning
             * their boxes' bottoms drops the plus 5px below the arrow. Centring
             * both puts the two glyphs on one line.
             */
            className="col-start-1 row-start-2 justify-self-start self-center"
            disabled={inputDisabled}
            onSelect={setContextKind}
            /*
             * The blur into the menu deliberately holds the composer open, and
             * nothing else fires once focus leaves the menu again - Ark returns
             * it to the trigger, not to the textarea - so dismissing the menu
             * left the row tall, empty and unfocused until something else was
             * clicked. This is what closes that gap.
             *
             * Guarded on the textarea rather than cleared outright: dismissing
             * the menu by clicking back into the message is a close too, and the
             * focus that follows it must not be undone.
             */
            onOpenChange={(open) => {
              if (!open) setIsFocused(document.activeElement === textareaRef.current);
            }}
          />
        )}
        <textarea
          ref={textareaRef}
          id="agent-chat-message-input"
          aria-label={t('prop-chat.message-input')}
          aria-describedby={inputHintId}
          className={clsx(
            'min-w-0 flex-1 resize-none overscroll-contain bg-transparent font-[var(--ds-typography-font-definitions-font-family-body)] text-[length:var(--ds-typography-paragraph-13-regular-font-size)] font-[var(--ds-typography-paragraph-13-regular-font-weight)] leading-[var(--ds-typography-paragraph-13-regular-line-height)] tracking-[var(--ds-typography-paragraph-13-regular-letter-spacing)] text-[var(--ds-color-text-primary)] outline-none placeholder:text-[var(--ds-color-text-tertiary)]',
            isDrawerExpanded
              ? 'col-span-2 row-start-1 max-h-[40vh] self-stretch overflow-y-auto'
              : /*
                 * Its own height, not the row's: a textarea draws its text from
                 * the top, so stretching this one to the full 24px content box
                 * left the 19.5px line sitting ~2px above the centre the plus
                 * and the send CTA are on. At its natural single-line height
                 * the row's items-center centres the line box itself, which is
                 * what puts all three on one line.
                 */
                'overflow-hidden',
          )}
          rows={1}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => setIsFocused(true)}
          disabled={inputDisabled}
          placeholder={t('prop-chat.drawer-placeholder')}
          // Focus is not pulled back when the answer finishes and the field is
          // re-enabled: disabling it drops focus to the body, and grabbing it
          // again lands the caret - and the expanded composer - on someone who
          // has since clicked into the conversation or a source.
          onBlur={(event) => {
            /*
             * Opening the add-context menu blurs the composer, and collapsing
             * on that dropped a box the user had just expanded back to one
             * line behind the menu. Focus landing on the menu is not the user
             * leaving the composer, so the height stays - and the menu's own
             * close is what clears it again.
             *
             * Read off the event rather than from the menu's own open state:
             * Ark opens on pointer-down, so whether that state has flushed by
             * the time this runs is not something to depend on.
             */
            const intoAddContext = (event.relatedTarget as HTMLElement | null)?.closest(
              '[data-add-context]',
            );

            if (!intoAddContext) setIsFocused(false);
          }}
        />
        <Button
          aria-label={t('prop-chat.send-message')}
          variant="ghost"
          size="mini"
          disabled={inputDisabled || !hasText}
          className={clsx(
            'size-[calc(var(--ds-space-6)+var(--ds-space-0-5))] min-h-0 shrink-0 rounded-[var(--ds-radius-4)] p-[var(--ds-space-1-25)] transition-colors duration-300 motion-reduce:transition-none enabled:hover:bg-[var(--ds-color-bg-brand)] enabled:hover:text-[var(--ds-color-text-invert)]',
            // Grid placement, inert while the row is a flex line.
            'col-start-2 row-start-2 justify-self-end self-center',
            !hasText
              ? 'bg-[var(--ds-color-bg-secondary)] text-[var(--ds-color-bg-brand)]'
              : 'bg-[var(--ds-color-bg-brand)] text-[var(--ds-color-text-invert)]',
          )}
          onClick={sendMessage}
          onKeyDown={onSendButtonKeyDown}
        >
          <ArrowUp aria-hidden="true" className="size-[var(--ds-space-4)]" />
        </Button>
      </div>
      {isContextEnabled && contextKind !== null && (
        <AddContextModal
          kind={contextKind}
          onClose={() => setContextKind(null)}
          onSubmit={(selection) => {
            setContext(selection);
            onAddContext?.(selection);
            setContextKind(null);
          }}
        />
      )}
    </div>
  );
};

export const ChatInput = memo(ChatInputComponent);
